import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type {
  ChartDataType,
  ProductDetailResponseType,
  ProductOverviewResponseType,
  DailyDistributionResponseType,
  SkuReviewProductItemType,
  PaginationType,
  HubDistributionResponseType,
} from '../../@types/dpTypes'
import {
  fetchSkuReviewProductList,
  fetchProductDetail,
  fetchProductOverview,
  fetchProductTrend,
  fetchProductDailyDistribution,
  fetchProductHubSummary,
  fetchProductHubList,
  markProductReview,
  fetchSkuReviewHubDistribution,
  fetchSkuReviewSummary,
} from './skuDetailsReviewThunk'

const ERROR_MSG = 'An error occurred'

export interface SkuDetailsReviewStateType {
  isLoading: boolean
  isLoadingDetail: boolean
  isLoadingOverview: boolean
  isLoadingTrend: boolean
  isLoadingDailyDistribution: boolean
  dailyDistributionLoadingMonth: string
  isLoadingGetProductList: boolean
  error: string | null
  productDetail: Nullable<ProductDetailResponseType['data']>
  productOverview: Nullable<ProductOverviewResponseType['data']>
  productTrend: Nullable<ChartDataType[]>
  dailyDistribution: Nullable<DailyDistributionResponseType['data']>
  dailyDistributionFetchedMonths: string[]
  dailyDistributionRequestKey: string
  dailyDistributionNextCursorByMonth: Record<string, string>
  dailyDistributionPagination: Nullable<PaginationType>
  skuReviewProductList: SkuReviewProductItemType[]
  skuReviewProductListPagination: Nullable<PaginationType>
  hubSummary: Nullable<{ total_need_review: number; total_reviewed: number }>
  skuSummary: Nullable<{ total_need_review: number; total_reviewed: number }>
  hubList: Nullable<{ id: number; name: string; is_review: boolean }[]>
  hubListPagination: Nullable<PaginationType>
  categoryList: Nullable<{ id: number; name: string }[]>
  dateRange: {
    startDate: string
    endDate: string
  }
  hubDistribution: Nullable<HubDistributionResponseType['data']>
  hubDistributionPagination: Nullable<PaginationType>
  detailsMetaData: {
    productId?: number
    productName?: string
    hubId?: number
    hubName?: string
    reviewIndex?: number
    isReview?: boolean
  }
}

const initialState: SkuDetailsReviewStateType = {
  isLoading: false,
  isLoadingDetail: false,
  isLoadingOverview: false,
  isLoadingTrend: false,
  isLoadingDailyDistribution: false,
  dailyDistributionLoadingMonth: '',
  isLoadingGetProductList: false,
  error: null,
  productDetail: null,
  productOverview: null,
  productTrend: null,
  dailyDistribution: null,
  dailyDistributionFetchedMonths: [],
  dailyDistributionRequestKey: '',
  dailyDistributionNextCursorByMonth: {},
  dailyDistributionPagination: null,
  skuReviewProductList: [],
  skuReviewProductListPagination: null,
  hubSummary: null,
  skuSummary: null,
  hubList: null,
  hubListPagination: null,
  categoryList: null,
  dateRange: {
    startDate: '',
    endDate: '',
  },
  detailsMetaData: {
    productId: undefined,
    productName: undefined,
    hubId: undefined,
    hubName: undefined,
    reviewIndex: undefined,
    isReview: undefined,
  },
  hubDistribution: null,
  hubDistributionPagination: null,
}

export const sliceName = 'skuDetailsReview'

const skuDetailsReviewSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    reset: () => initialState,
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setSkuDetailsReviewDateRange: (
      state,
      action: PayloadAction<{ startDate: string; endDate: string }>,
    ) => {
      const hasDateRangeChanged =
        state.dateRange.startDate !== action.payload.startDate ||
        state.dateRange.endDate !== action.payload.endDate
      state.dateRange = action.payload
      if (hasDateRangeChanged) {
        state.isLoadingDailyDistribution = false
        state.dailyDistribution = null
        state.dailyDistributionFetchedMonths = []
        state.dailyDistributionRequestKey = ''
        state.dailyDistributionNextCursorByMonth = {}
        state.dailyDistributionLoadingMonth = ''
        state.dailyDistributionPagination = null
      }
    },
    resetDailyDistribution: (state, action: PayloadAction<string>) => {
      state.isLoadingDailyDistribution = false
      state.dailyDistribution = null
      state.dailyDistributionFetchedMonths = []
      state.dailyDistributionRequestKey = action.payload
      state.dailyDistributionNextCursorByMonth = {}
      state.dailyDistributionLoadingMonth = ''
      state.dailyDistributionPagination = null
    },
    setDetailsMetaData: (
      state,
      action: PayloadAction<{
        productId?: number
        productName?: string
        hubId?: number
        hubName?: string
        reviewIndex?: number
        isReview?: boolean
      }>,
    ) => {
      state.detailsMetaData = action.payload
    },
    setHubReview: (state, action: PayloadAction<{ hubId: number }>) => {
      state.hubSummary = {
        total_need_review: state.hubSummary?.total_need_review ?? 0,
        total_reviewed: (state.hubSummary?.total_reviewed ?? 0) + 1,
      }
      const hubList = state.hubList ?? []
      const idx = hubList.findIndex((h) => h.id === action.payload.hubId)
      if (idx !== -1) {
        const reviewed = { ...hubList[idx], is_review: true }
        const updatedHubList = [...hubList.slice(0, idx), ...hubList.slice(idx + 1), reviewed]
        state.hubList = updatedHubList
        const allReviewed = updatedHubList.every((h) => h.is_review)
        if (allReviewed) {
          state.skuSummary = {
            total_need_review: state.skuSummary?.total_need_review ?? 0,
            total_reviewed: (state.skuSummary?.total_reviewed ?? 0) + 1,
          }
        }
      }
    },
    setProductReview: (state, action: PayloadAction<{ productId: number }>) => {
      state.skuSummary = {
        total_need_review: state.skuSummary?.total_need_review ?? 0,
        total_reviewed: (state.skuSummary?.total_reviewed ?? 0) + 1,
      }
      const list = state.skuReviewProductList
      const idx = list.findIndex((p) => p.id === action.payload.productId)
      if (idx !== -1) {
        const reviewed = { ...list[idx], is_review: true }
        state.skuReviewProductList = [...list.slice(0, idx), ...list.slice(idx + 1), reviewed]
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // SkuReviewProductList
      .addCase(fetchSkuReviewProductList.pending, (state) => {
        state.isLoadingGetProductList = true
        state.error = null
      })
      .addCase(fetchSkuReviewProductList.fulfilled, (state, action) => {
        state.isLoadingGetProductList = false
        const incoming = action.payload?.data ?? []
        state.skuReviewProductList = action.meta.arg.append
          ? [...state.skuReviewProductList, ...incoming]
          : incoming
        state.skuReviewProductListPagination = action.payload?.pagination ?? null
        state.error = null
      })
      .addCase(fetchSkuReviewProductList.rejected, (state, action) => {
        state.isLoadingGetProductList = false
        state.error = action.error.message || ERROR_MSG
      })
      // ProductDetail
      .addCase(fetchProductDetail.pending, (state) => {
        state.isLoadingDetail = true
        state.error = null
      })
      .addCase(fetchProductDetail.fulfilled, (state, action) => {
        state.isLoadingDetail = false
        state.productDetail = action.payload?.data ?? null
        state.error = null
      })
      .addCase(fetchProductDetail.rejected, (state, action) => {
        state.isLoadingDetail = false
        state.error = action.error.message || ERROR_MSG
      })
      // ProductOverview
      .addCase(fetchProductOverview.pending, (state) => {
        state.isLoadingOverview = true
        state.error = null
      })
      .addCase(fetchProductOverview.fulfilled, (state, action) => {
        state.isLoadingOverview = false
        state.productOverview = action.payload?.data ?? null
        state.error = null
      })
      .addCase(fetchProductOverview.rejected, (state, action) => {
        state.isLoadingOverview = false
        state.error = action.error.message || ERROR_MSG
      })
      // ProductTrend
      .addCase(fetchProductTrend.pending, (state) => {
        state.isLoadingTrend = true
        state.error = null
      })
      .addCase(fetchProductTrend.fulfilled, (state, action) => {
        state.isLoadingTrend = false
        state.productTrend = action.payload?.data ?? null
        state.error = null
      })
      .addCase(fetchProductTrend.rejected, (state, action) => {
        state.isLoadingTrend = false
        state.error = action.error.message || ERROR_MSG
      })
      // ProductDailyDistribution
      .addCase(fetchProductDailyDistribution.pending, (state, action) => {
        state.isLoadingDailyDistribution = true
        state.dailyDistributionLoadingMonth = action.meta.arg.monthKey
        state.error = null
      })
      .addCase(fetchProductDailyDistribution.fulfilled, (state, action) => {
        if (action.meta.arg.requestKey !== state.dailyDistributionRequestKey) return
        state.isLoadingDailyDistribution = false
        state.dailyDistributionLoadingMonth = ''

        const incoming = action.payload?.data ?? []
        const fetchedMonth = action.meta.arg.monthKey
        const existing = state.dailyDistribution ?? []
        const incomingDates = new Set(incoming.map((item) => item.date))
        state.dailyDistribution = [
          ...existing.filter((item) => !incomingDates.has(item.date)),
          ...incoming,
        ].sort((a, b) => a.date.localeCompare(b.date))
        if (fetchedMonth && !state.dailyDistributionFetchedMonths.includes(fetchedMonth)) {
          state.dailyDistributionFetchedMonths.push(fetchedMonth)
        }
        const nextCursor = action.payload?.pagination?.next_cursor
        state.dailyDistributionNextCursorByMonth[fetchedMonth] = nextCursor || ''
        state.dailyDistributionPagination = action.payload?.pagination ?? null
        state.error = null
      })
      .addCase(fetchProductDailyDistribution.rejected, (state, action) => {
        if (action.meta.arg.requestKey !== state.dailyDistributionRequestKey) return
        state.isLoadingDailyDistribution = false
        state.dailyDistributionLoadingMonth = ''
        state.error = action.error.message || ERROR_MSG
      })
      // ProductHubSummary
      .addCase(fetchProductHubSummary.pending, (state) => {
        state.isLoadingDetail = true
        state.error = null
      })
      .addCase(fetchProductHubSummary.fulfilled, (state, action) => {
        state.isLoadingDetail = false
        state.hubSummary = action.payload ?? null
        state.error = null
      })
      .addCase(fetchProductHubSummary.rejected, (state, action) => {
        state.isLoadingDetail = false
        state.error = action.error.message || ERROR_MSG
      })
      // ProductHubList
      .addCase(fetchProductHubList.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchProductHubList.fulfilled, (state, action) => {
        state.isLoading = false
        state.hubList = action.payload?.data ?? null
        state.hubListPagination = action.payload?.pagination ?? null
        state.error = null
      })
      .addCase(fetchProductHubList.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || ERROR_MSG
      })
      // MarkProductReview
      .addCase(markProductReview.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(markProductReview.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(markProductReview.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || ERROR_MSG
      })
      // SkuReviewHubDistribution
      .addCase(fetchSkuReviewHubDistribution.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchSkuReviewHubDistribution.fulfilled, (state, action) => {
        state.isLoading = false
        const incoming = action.payload?.data ?? null
        if (action.meta.arg.append && state.hubDistribution && incoming) {
          state.hubDistribution = {
            ...incoming,
            hubs: [...state.hubDistribution.hubs, ...incoming.hubs],
          }
        } else {
          state.hubDistribution = incoming
        }
        state.hubDistributionPagination = action.payload?.pagination ?? null
        state.error = null
      })
      .addCase(fetchSkuReviewHubDistribution.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || ERROR_MSG
      })
      // SkuReviewSummary
      .addCase(fetchSkuReviewSummary.pending, (state) => {
        state.isLoadingDetail = true
        state.error = null
      })
      .addCase(fetchSkuReviewSummary.fulfilled, (state, action) => {
        state.isLoadingDetail = false
        state.skuSummary = action.payload?.data ?? null
        state.error = null
      })
      .addCase(fetchSkuReviewSummary.rejected, (state, action) => {
        state.isLoadingDetail = false
        state.error = action.error.message || ERROR_MSG
      })
  },
})

export const {
  setLoading,
  reset,
  setSkuDetailsReviewDateRange,
  setDetailsMetaData,
  setHubReview,
  setProductReview,
  resetDailyDistribution,
} = skuDetailsReviewSlice.actions

export default skuDetailsReviewSlice.reducer
